import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import config from 'src/config';
import { useToast } from 'src/context/ToastContext';
import ApiError from 'src/api/ApiError';
import { ManualAmount } from './useManualAmounts';

interface DeleteResult {
  id: string;
  success: boolean;
  error?: string;
}

interface BulkDeleteState {
  isLoading: boolean;
  results: DeleteResult[];
  successCount: number;
  failureCount: number;
}

/**
 * Internal mutation for deleting a single manual amount by ID.
 * This is a low-level mutation that doesn't show toasts - used by the bulk delete.
 */
function useDeleteManualAmountMutation() {
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: async ({ id }) => {
      const response = await fetch(
        `${config.apiBaseUrl}/rebates/manual-amounts/${id}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        const errorData: { message?: string } = await response
          .json()
          .catch(() => ({}));
        throw new ApiError(
          response.status,
          errorData.message || 'Failed to delete manual amount',
        );
      }
    },
  });
}

/**
 * Hook for bulk deleting manual amounts.
 * Executes deletions sequentially using RPC mutation pattern and reports success/failure counts.
 */
export function useBulkDeleteManualAmounts() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [state, setState] = useState<BulkDeleteState>({
    isLoading: false,
    results: [],
    successCount: 0,
    failureCount: 0,
  });

  const deleteManualAmountMutation = useDeleteManualAmountMutation();

  const bulkDelete = useCallback(
    async (manualAmounts: ManualAmount[]) => {
      if (manualAmounts.length === 0) return;

      setState({
        isLoading: true,
        results: [],
        successCount: 0,
        failureCount: 0,
      });

      const results: DeleteResult[] = [];

      // Delete sequentially to avoid overwhelming the API
      for (const amount of manualAmounts) {
        try {
          await deleteManualAmountMutation.mutateAsync({ id: amount.id });
          results.push({
            id: amount.id,
            success: true,
          });
        } catch (error) {
          results.push({
            id: amount.id,
            success: false,
            error:
              error instanceof ApiError
                ? error.message
                : error instanceof Error
                  ? error.message
                  : 'Unknown error occurred',
          });
        }
      }

      const successCount = results.filter((r) => r.success).length;
      const failureCount = results.filter((r) => !r.success).length;

      setState({
        isLoading: false,
        results,
        successCount,
        failureCount,
      });

      // Show toast with results
      if (failureCount === 0) {
        showToast({
          message: `Successfully deleted ${successCount} manual amount${successCount !== 1 ? 's' : ''}`,
          title: 'Success',
          color: 'success',
        });
      } else if (successCount === 0) {
        showToast({
          message: 'Failed to delete manual amounts',
          title: 'Error',
          color: 'error',
        });
      } else {
        showToast({
          message: `Deleted ${successCount} of ${results.length} manual amounts. ${failureCount} failed.`,
          title: 'Partial Success',
          color: 'warning',
        });
      }

      // Invalidate manual amounts queries to refresh the data
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          typeof query.queryKey[0] === 'string' &&
          query.queryKey[0].includes('rebates/manual-amounts'),
      });

      return results;
    },
    [queryClient, showToast, deleteManualAmountMutation],
  );

  const reset = useCallback(() => {
    setState({
      isLoading: false,
      results: [],
      successCount: 0,
      failureCount: 0,
    });
  }, []);

  return {
    bulkDelete,
    reset,
    isLoading: state.isLoading,
    results: state.results,
    successCount: state.successCount,
    failureCount: state.failureCount,
  };
}
